import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { ApolloError } from "@apollo/client/errors";
import AddParticipationModal from "@/pages/people/[pgaId]/education/professional-development/_components/AddParticipationModal";
import {
  UPDATE_PDR_CREDIT_MUTATION,
  GET_PDR_HISTORY_QUERY,
  GET_PDR_REQUIREMENTS_QUERY
} from "@/pages/people/[pgaId]/education/queries";
import { GET_PDR_HISTORY_DETAILS_QUERY } from "@/pages/people/[pgaId]/education/professional-development/[activityId]/queries";
import { formatPDRHistoryDetailsForForm } from "@/pages/people/[pgaId]/education/professional-development/_utils";
import { showSnackbar } from "@/lib/snackbarUtils";
import type { FieldValues } from "@/pages/people/[pgaId]/education/professional-development/_components/AddParticipationModal";
import { excludedActivityCodes } from "@/pages/people/[pgaId]/education/professional-development/_utils";
import TypeLevelEditParticipationModal from "./TypeLevelEditParticipationModal";

export default function EditParticipationModal({
  onDismiss,
  pgaId,
  activityId,
  activityParticipationId,
  isTypeLevelParticipation,
  defaultValues,
  skipPdrQuery,
  additionalRefetchQueries
}: {
  onDismiss: () => void;
  pgaId: string;
  activityId: string;
  activityParticipationId?: string;
  isTypeLevelParticipation?: boolean;
  defaultValues?: any;
  /** When true (activity-types context), use simplified mutation payload */
  skipPdrQuery?: boolean;
  /** Additional queries to refetch on success (e.g. GET_ACTIVITY_PARTICIPATION for activity-types context) */
  additionalRefetchQueries?: any[];
}) {
  if (isTypeLevelParticipation) {
    if (!activityParticipationId) {
      onDismiss();
      return null;
    }

    const typeLevelRefetchQueries =
      skipPdrQuery && additionalRefetchQueries?.length
        ? additionalRefetchQueries
        : [GET_PDR_HISTORY_QUERY, GET_PDR_REQUIREMENTS_QUERY, ...(additionalRefetchQueries || [])];

    return (
      <TypeLevelEditParticipationModal
        onDismiss={onDismiss}
        activityParticipationId={activityParticipationId}
        additionalRefetchQueries={typeLevelRefetchQueries}
      />
    );
  }

  return (
    <LegacyEditParticipationModal
      onDismiss={onDismiss}
      pgaId={pgaId}
      activityId={activityId}
      defaultValues={defaultValues}
      skipPdrQuery={skipPdrQuery}
      additionalRefetchQueries={additionalRefetchQueries}
    />
  );
}

function LegacyEditParticipationModal({
  onDismiss,
  pgaId,
  activityId,
  defaultValues,
  skipPdrQuery,
  additionalRefetchQueries
}: {
  onDismiss: () => void;
  pgaId: string;
  activityId: string;
  defaultValues?: any;
  skipPdrQuery?: boolean;
  additionalRefetchQueries?: any[];
}) {
  const { data, loading } = useQuery(GET_PDR_HISTORY_DETAILS_QUERY, {
    variables: { id: activityId, pgaId },
    skip: !!defaultValues || !!skipPdrQuery
  });
  const defaultFormValues = defaultValues ?? (data ? formatPDRHistoryDetailsForForm(data, pgaId) : undefined);
  const hasAssociatedActivity = skipPdrQuery || !!data?.pdrHistoryDetails?.activityParticipation?.id;

  const refetchQueries = [
    GET_PDR_HISTORY_DETAILS_QUERY,
    GET_PDR_HISTORY_QUERY,
    GET_PDR_REQUIREMENTS_QUERY,
    ...(additionalRefetchQueries || [])
  ];
  const [updatePdrCredit] = useMutation(UPDATE_PDR_CREDIT_MUTATION, {
    refetchQueries
  });
  const [activityCode, setActivityCode] = useState<string | null>(defaultFormValues?.activityCode);
  const [bannerError, setBannerError] = useState("");

  useEffect(() => {
    setActivityCode(defaultFormValues?.activityCode);
  }, [defaultFormValues]);

  const onSubmit = async (values: FieldValues) => {
    try {
      const {
        data: {
          updatePdrCredit: { success, message }
        }
      } = await updatePdrCredit({
        variables: {
          input: {
            id: activityId,
            pgaId,
            title: values.title,
            description: values.activityDescription,
            ...(activityCode === "M06" && values.totalHours && { hoursServed: Number(values.totalHours) }),
            ...(activityCode === "M09"
              ? { publishedOn: values.publishedAt }
              : {
                  startedOn: values.startDate,
                  endedOn: values.endDate,
                  location: values.location
                }),
            ...(excludedActivityCodes(activityCode) && {
              ...(values.creditsRequested && { creditsAppliedFor: Number(values.creditsRequested) }),
              ...(values.creditsAwarded && { creditsAccepted: Number(values.creditsAwarded) })
            })
          }
        }
      });
      if (success) {
        showSnackbar("Participation updated");
        onDismiss();
      } else {
        setBannerError(message);
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    }
  };

  if (loading && !defaultValues) {
    return <div>Loading...</div>;
  }

  return (
    <AddParticipationModal
      onDismiss={onDismiss}
      defaultValues={defaultFormValues}
      pgaId={pgaId}
      onSubmit={onSubmit}
      activityCode={activityCode}
      setActivityCode={setActivityCode}
      bannerError={bannerError}
      hasAssociatedActivity={hasAssociatedActivity}
    />
  );
}
